import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {LogInApiResponse, LogInData, SignInResult} from '../types';

import {useCompleteLogin} from './useCompleteLogin';
import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../hooks/useApiWrite';
import {Post} from '../../../../lib/api/POST/Post';
import {PostRoutes} from '../../../../lib/api/POST/PostRoutes';
import {ApiResponseError} from '../../../../lib/api/v1/types';
import {ApiSerializer} from '../../../../serializers/ApiSerializer';
import {ApplicantRoleData, CompanyAdminRoleData, User} from '../../../../types/User';

const serializeLoginResponse = (data: unknown): SignInResult => {
  const response = ApiSerializer.toCamelCase<LogInApiResponse>(data);
  const {user, meta} = response;

  let roleData: ApplicantRoleData | CompanyAdminRoleData | undefined;

  if (user.role.type === 'applicant') {
    roleData = {
      ...user.role.data,
      applicantId: user.role.data.applicantId ?? '',
      talentSurveyId: user.role.data.talentSurveyId ?? '',
      onboardingCompleted: user.role.data.applicantOnboardingCompleted ?? false,
    } as ApplicantRoleData;
  }

  if (user.role.type === 'company_admin') {
    roleData = {
      ...user.role.data,
      avatarUrl: user.role.data.avatarUrl ?? '',
      companyId: user.role.data.companyId ?? '',
      companyAdminId: user.role.data.companyAdminId ?? '',
      contractSigned: user.role.data.contractSigned ?? false,
    } as CompanyAdminRoleData;
  }

  const twoFactorAuthRequired = !!meta.twoFactorAuthRequired;

  return {
    user: {
      ...user,
      id: user.resourceId,
      role: {
        type: user.role.type,
        data: roleData,
      },
      token: meta.authToken,
      refreshToken: meta.refreshToken,
      // Sign-in exposes this as meta.two_factor_auth_required; there is no users/me field yet.
      twoFactorEnabled: twoFactorAuthRequired,
    },
    twoFactorAuthRequired,
  };
};

interface UseLogInArgs {
  /**
   *
   * Called instead of completing the login when the API asks for a second factor. The user is
   * handed over unsaved: no cookie is written and no user context is set until the OTP validates.
   */
  onTwoFactorRequired?: (user: User) => void;
}

export const useLogIn = ({onTwoFactorRequired}: UseLogInArgs = {}) => {
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);
  const {completeLogin} = useCompleteLogin();

  const onSuccess = ({user, twoFactorAuthRequired}: SignInResult) => {
    if (twoFactorAuthRequired && onTwoFactorRequired) {
      return onTwoFactorRequired(user);
    }

    completeLogin(user);
  };

  const onError = (error?: ApiResponseError) => {
    const isAccountLocked = error?.extra_details?.toLowerCase().includes('locked');
    const message = isAccountLocked
      ? t('registration.logIn.accountLocked')
      : t('registration.logIn.invalidCredentials');

    dispatchMessage(message, 'error');
  };

  const {makeRequest, isLoading} = useApiWrite<Post.SignIn, SignInResult>({
    method: 'post',
    path: PostRoutes[Post.SignIn],
    onSuccess,
    onError,
    disableLoadingOverlay: true,
    camelCaseOverride: serializeLoginResponse,
  });

  const logIn = (credentials: LogInData) => {
    makeRequest(credentials);
  };

  return {logIn, isLoading};
};
